'use client';

import { useParams, useRouter } from 'next/navigation';

import { getCustomerGuest, requestOtp } from '@/actions';
import { useOtpVerificationModal } from '@/components/OtpVerificationModal';
import { API_URL } from '@/config';
import { useWhitelabelConfig } from '@/hooks/useWhitelabelConfig';
import { toast } from '@/libs';
import { useHumanPass } from '@/providers/HumanPassProvider';
import { useCustomerStore, useDrawerStore } from '@/stores';
import { OtpScope, PathAPI, ValidationKey } from '@/types/enums';
import { formatErrorMessage } from '@/types/models/ApiError';
import {
  getInAppBrowserInfo,
  logger,
  parseBoolean,
  parseDigits,
  RECEIPT_PREVIEW_PATH,
  storeReceiptPreview,
  triggerBlobDownload,
  triggerServerDownload,
  isHumanPassDenied,
} from '@/utils';
import { addDDIToPhone } from '@/utils/formatters';

import { DownloadTicket } from '../../../DownloadTicket';

type Ticket = {
  id: string;
  sequence: string;
};

type DownloadResult = { ok: true; html: string } | { ok: false; status: number };

const GENERIC_DOWNLOAD_ERROR =
  'Não foi possível baixar o comprovante. Tente novamente mais tarde.';

export const useCartRaffleAccordion = () => {
  // O widget vive no HumanPassProvider, global. Aqui só o gatilho de re-desafio.
  const { refresh: refreshHumanPass } = useHumanPass();

  const params = useParams();
  const router = useRouter();
  const raffleId = params['raffle-id'] as string | undefined;

  const openDrawer = useDrawerStore((state) => state.openDrawer);
  const replaceDrawer = useDrawerStore((state) => state.replaceDrawer);
  const closeCurrentDrawer = useDrawerStore((state) => state.closeCurrentDrawer);
  const drawers = useDrawerStore((state) => state.drawers);

  const customerData = useCustomerStore((state) => state.customerData);
  const { getValue } = useWhitelabelConfig();
  const { openOtpVerificationModal } = useOtpVerificationModal();

  const openCartRaffleAccordionDrawer = (ticket: Ticket) => {
    const hasOpenDrawer = drawers.length > 0;

    if (!ticket.id && !ticket.sequence) return;

    const cartRaffleDrawerConfig = {
      title: 'Comprovante',
      Component: () => DownloadTicket({ ticket }),
      drawerType: 'cart-raffle-accordion',
      drawerHeaderOptions: { className: 'text-center pt-4' },
    } as const;

    if (hasOpenDrawer) {
      replaceDrawer(cartRaffleDrawerConfig);
    } else {
      openDrawer(cartRaffleDrawerConfig);
    }
  };

  const buildReceiptUrl = (ticketId: string) =>
    `${API_URL}${PathAPI.downloadTicket}?raffleTicketId=${ticketId}${
      raffleId ? `&raffleId=${raffleId}` : ''
    }`;

  const downloadReceipt = async (
    ticketId: string,
    otpCode?: string,
  ): Promise<DownloadResult> => {
    const url = buildReceiptUrl(ticketId);

    const sanitizedOtpCode = otpCode ? parseDigits(otpCode) : '';
    const response = sanitizedOtpCode
      ? await fetch(url, { headers: { 'x-otp-code': sanitizedOtpCode } })
      : await fetch(url);

    if (!response.ok) {
      return { ok: false, status: response.status };
    }

    return { ok: true, html: await response.text() };
  };

  const deliverReceipt = (
    html: string,
    ticketId: string,
    { otpVerified }: { otpVerified: boolean },
  ) => {
    const { isInAppBrowser } = getInAppBrowserInfo();

    if (isInAppBrowser) {
      if (!otpVerified) {
        triggerServerDownload(buildReceiptUrl(ticketId));
        return;
      }

      if (storeReceiptPreview({ ticketId, html })) {
        router.push(RECEIPT_PREVIEW_PATH);
        return;
      }
    }

    triggerBlobDownload(
      new Blob([html], { type: 'text/html' }),
      `receipt-${ticketId}.html`,
    );
  };

  const resolveCustomerId = async (): Promise<string | undefined> => {
    if (customerData?.customerId) return customerData.customerId;

    const customerDocument = customerData?.document;
    const phone = customerData?.phone;

    if (customerDocument && phone) {
      try {
        const guest = await getCustomerGuest(
          parseDigits(customerDocument),
          addDDIToPhone(parseDigits(phone)),
        );

        if (isHumanPassDenied(guest)) {
          refreshHumanPass();
          return undefined;
        }

        return guest?.customerId;
      } catch (error) {
        logger.error('Error resolving customer for OTP:', error);
      }
    }

    return undefined;
  };

  const startOtpFlow = async (ticketId: string) => {
    const customerId = await resolveCustomerId();

    if (!customerId) {
      toast.error('Não foi possível identificar seu cadastro para enviar o código.');
      return;
    }

    const { data, error } = await requestOtp({
      scope: OtpScope.RECEIPT_DOWNLOAD,
      customerId,
    });

    if (error || !data) {
      toast.error(
        'Não foi possível enviar o código por SMS. Tente novamente mais tarde.',
      );
      return;
    }

    closeCurrentDrawer();

    let receiptHtml = '';

    openOtpVerificationModal({
      maskedPhone: data.maskedPhone,
      onVerify: async (code) => {
        try {
          const result = await downloadReceipt(ticketId, code);
          if (result.ok) {
            receiptHtml = result.html;
            return 'success';
          }
          if (result.status === 403) return 'invalid';
          if (result.status === 429) return 'blocked';
          return 'error';
        } catch {
          return 'error';
        }
      },
      onSuccess: () => deliverReceipt(receiptHtml, ticketId, { otpVerified: true }),
      onResend: async () => {
        const resent = await requestOtp({
          scope: OtpScope.RECEIPT_DOWNLOAD,
          customerId,
        });
        return resent.data?.maskedPhone;
      },
    });
  };

  const handleDownloadTicketWithFallback = async (ticketId: string) => {
    if (!ticketId) return;

    try {
      const otpRequired = parseBoolean(
        getValue<boolean>(ValidationKey.VALIDATION_OTP_REQUIRED_DOWNLOAD_RECEIPT),
        false,
      );

      const firstAttempt = await downloadReceipt(ticketId);
      if (firstAttempt.ok) {
        deliverReceipt(firstAttempt.html, ticketId, { otpVerified: false });
        return;
      }

      if (firstAttempt.status === 428) {
        if (!otpRequired) {
          logger.warn(
            'Receipt download returned 428 but the whitelabel OTP flag is off/unexposed on the client.',
          );
        }
        await startOtpFlow(ticketId);
        return;
      }

      toast.error(GENERIC_DOWNLOAD_ERROR);
    } catch (error) {
      toast.error(formatErrorMessage(error, GENERIC_DOWNLOAD_ERROR));
    }
  };

  return { openCartRaffleAccordionDrawer, handleDownloadTicketWithFallback };
};
